import { IRequestAuth } from "core/api";
import { clearState as clearStateAction } from "core/store/enhancers/clearStateEnhance";
import * as jwt_decode from "jwt-decode";
import { all, call, fork, put, select } from "redux-saga/effects";
import { historyPushRequest, saga as historySaga } from "./history";
import {
  ISagaDependencies as ILoginSagaDependencies,
  saga as loginSaga,
  tokenSet,
} from "./login";
import { actions as mainActions, saga as mainSaga } from "./main";
import { selectToken as selectTokenSelector } from "./selectors";
import {
  ISagaDependencies as IRegisterSagaDependencies,
  saga as registerSaga,
} from "./register";
import {
  IMainSagaDependencies as IReviewerSagaDependencies,
  saga as reviwersSaga,
} from "./reviewers";
import {
  IMainSagaDependencies as ITechnicianSagaDependencies,
  saga as techniciansSaga,
} from "./technicians";
import safeSessionStorage from 'core/store/safe-session-storage'

/**
 * Wraps select for selectToken to keep conventions for dependencies to be invoked via `yield call(dependency)`
 */
export function* selectToken() {
  return yield select(selectTokenSelector);
}

/**
 * Wraps dispatch of clearState to keep conventions for dependencies to be invoked via `yield call(dependency)`
 */
export function* clearState() {
  yield call(() => safeSessionStorage.clear());
  yield put(clearStateAction());
}

/**
 * Wraps an API request with authentication logic.
 * Calls the API request fn with the token from the redux store as the first argument.
 * Sets the new token if given in the response at `response.headers.Authorization` or
 * `response.headers.authorization`.
 *
 * ex: `yield call(withAuth, myApiCall, arg1ToMyApiCall)`
 *
 * @param fn some authenticated API request with token as first arg
 * @param args args to pass to authenticated API request
 */
export function* withAuth(fn: () => any, ...args: any[]) {
  const token = yield call(selectToken);
  const res = yield call(fn, token, ...args);
  const decodedToken = jwt_decode(token) as any;
  const { role } = decodedToken;
  const authHeader =
    res &&
    res.headers &&
    (res.headers.Authorization || res.headers.authorization);
  if (
    authHeader &&
    typeof authHeader === "string" &&
    authHeader.startsWith("Bearer ")
  ) {
    const newToken = authHeader.replace(/^Bearer /, "");
    yield put(tokenSet({ token: newToken, role }));
  }
  return res;
}

/**
 * Wraps an API request with authentication logic.
 * Calls the API request fn with the token from the redux store in an IRequestAuth as the first argument.
 * Sets the new token if given in the response at `response.headers.Authorization` or
 * `response.headers.authorization`.
 *
 * ex: `yield call(withAuth, myApiCall, arg1ToMyApiCall)`
 *
 * @param fn some authenticated API request with an IRequestAuth as first arg
 * @param args args to pass to authenticated API request
 */
export function* withTokenAuth(
  fn: (auth: IRequestAuth, ...args: any[]) => any,
  ...args: any[]
) {
  const token = yield call(selectToken);
  const res = yield call(fn, { token }, ...args);
  const decodedToken = jwt_decode(token) as any;
  const { role } = decodedToken;
  const authHeader =
    res &&
    res.headers &&
    (res.headers.Authorization || res.headers.authorization);
  if (
    authHeader &&
    typeof authHeader === "string" &&
    authHeader.startsWith("Bearer ")
  ) {
    const newToken = authHeader.replace(/^Bearer /, "");
    yield put(tokenSet({ token: newToken, role }));
  }
  return res;
}


export function* withAuthNoRefresh(fn: (...args: any[]) => any, ...args: any[]) {
  const token = yield call(selectToken);
  const res = yield call(fn, token, ...args);
  return res;
}

function* onTokenExpired() {
  yield call(clearState);
  // TODO I18N
  yield put(
    mainActions.snackbarSetRequest(
      "Your session has expired, please log in and try again."
    )
  );
}

function* redirectTo(path: string) {
  yield put(historyPushRequest(path));
}

export default function* saga(): any {
  yield all([
    fork<ILoginSagaDependencies>(loginSaga, {
      selectToken,
      clearState,
      withAuth,
      onTokenExpired,
    }),
    fork(mainSaga),
    fork(historySaga),
    fork<IRegisterSagaDependencies>(registerSaga, { withAuth }),
    fork<IReviewerSagaDependencies>(reviwersSaga, {
      withAuth,
      withAuthNoRefresh,
      withTokenAuth,
      onTokenExpired,
      redirectTo,
    }),
    fork<ITechnicianSagaDependencies>(techniciansSaga, {
      withAuth,
      withTokenAuth,
      onTokenExpired,
      redirectTo,
    }),
  ]);
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./study_reviewer/src/store/saga.ts